import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { GoogleReviews } from '../components/GoogleReviews';
import { CallbackForm } from '../components/CallbackForm';
import { MapPin, MessageCircle, Mail, Calendar, Instagram, Phone } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const INSTAGRAM_URL = 'https://instagram.com/dentico_clinic';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1153">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1154" />
    </svg>;
}
export function ContactPage() {
  const {
    t,
    lang
  } = useTranslation();
  return <Layout data-id="element-1155">
      <div className="bg-teal-900 py-20 text-white" data-id="element-1156">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1157">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1158">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto" data-id="element-1159">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1160">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1161">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8" data-id="element-1162">
            <Card className="p-8" data-id="element-1163">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1164">
                {t('contact.getInTouch')}
              </h2>

              <div className="space-y-6" data-id="element-1165">
                {/* Address */}
                <div className="flex items-start gap-4" data-id="element-1166">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1167">
                    <MapPin className="w-6 h-6" data-id="element-1168" />
                  </div>
                  <div data-id="element-1169">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1170">
                      {t('contact.address')}
                    </h3>
                    <p className="text-gray-600" data-id="element-1171">
                      360–364 City Rd
                      <br data-id="element-1172" />
                      London EC1V 2PY
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4" data-id="element-1173">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1174">
                    <Phone className="w-6 h-6" data-id="element-1175" />
                  </div>
                  <div data-id="element-1176">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1177">
                      {lang === 'en' ? 'Phone' : lang === 'ua' ? 'Телефон' : 'Телефон'}
                    </h3>
                    <a href="tel:+447345197820" className="text-teal-600 hover:underline font-medium" data-id="element-1178">
                      07345 197820
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4" data-id="element-1179">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1180">
                    <MessageCircle className="w-6 h-6" data-id="element-1181" />
                  </div>
                  <div data-id="element-1182">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1183">
                      {t('contact.whatsapp')}
                    </h3>
                    <p className="text-gray-600 mb-2" data-id="element-1184">
                      {t('contact.whatsappDesc')}
                    </p>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1185">
                      <Button variant="outline" size="sm" className="gap-2" data-id="element-1186">
                        <MessageCircle className="w-4 h-4" data-id="element-1187" />
                        {t('contact.chatOnWhatsapp')}
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Telegram */}
                <div className="flex items-start gap-4" data-id="element-1188">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1189">
                    <TelegramIcon className="w-6 h-6" data-id="element-1190" />
                  </div>
                  <div data-id="element-1191">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1192">
                      Telegram
                    </h3>
                    <p className="text-gray-600 mb-2" data-id="element-1193">
                      {lang === 'en' ? 'Message us on Telegram' : lang === 'ua' ? 'Напишіть нам у Telegram' : 'Напишите нам в Telegram'}
                    </p>
                    <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-id="element-1194">
                      <Button variant="outline" size="sm" className="gap-2" data-id="element-1195">
                        <TelegramIcon className="w-4 h-4" data-id="element-1196" />
                        Telegram
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4" data-id="element-1197">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1198">
                    <Mail className="w-6 h-6" data-id="element-1199" />
                  </div>
                  <div data-id="element-1200">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1201">
                      {t('contact.email')}
                    </h3>
                    <a href="mailto:info@denticoclinic.co.uk" className="text-teal-600 hover:underline break-all" data-id="element-1202">
                      info@denticoclinic.co.uk
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4" data-id="element-1203">
                  <div className="p-2 bg-teal-50 rounded-lg text-teal-600" data-id="element-1204">
                    <Instagram className="w-6 h-6" data-id="element-1205" />
                  </div>
                  <div data-id="element-1206">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1207">
                      {t('contact.instagram')}
                    </h3>
                    <p className="text-gray-600 mb-2" data-id="element-1208">
                      {t('contact.instagramDesc')}
                    </p>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-teal-600 font-medium hover:underline" data-id="element-1209">
                      @dentico_clinic
                    </a>
                  </div>
                </div>
              </div>

              {/* Book Online Button */}
              <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-1210">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1211">
                  <Button fullWidth size="lg" className="gap-2" data-id="element-1212">
                    <Calendar className="w-5 h-5" data-id="element-1213" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
              </div>
            </Card>

            <Card className="p-8" data-id="element-1214">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1215">
                {t('contact.openingHours')}
              </h2>
              <div className="space-y-3" data-id="element-1216">
                <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-1217">
                  <span className="text-gray-600" data-id="element-1218">{t('footer.monFri')}</span>
                  <span className="font-medium text-gray-900" data-id="element-1219">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-1220">
                  <span className="text-gray-600" data-id="element-1221">{t('footer.saturday')}</span>
                  <span className="font-medium text-gray-900" data-id="element-1222">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1223">
                  <span className="text-gray-600" data-id="element-1224">{t('footer.sunday')}</span>
                  <span className="font-medium text-red-500" data-id="element-1225">
                    {t('footer.closed')}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Map */}
          <div className="lg:col-span-2" data-id="element-1226">
            <div className="bg-gray-100 rounded-2xl overflow-hidden h-full min-h-[500px] shadow-lg" data-id="element-1227">
              <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
              border: 0,
              minHeight: '500px'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" data-id="element-1228"></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Callback Form Section */}
      <section className="py-16 bg-gray-50" data-id="element-1229">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1230">
          <CallbackForm data-id="element-1231" />
        </div>
      </section>

      <GoogleReviews data-id="element-1232" />
    </Layout>;
}